import { useEffect, useState } from "react";
import {
  Button,
  Table,
  DatePicker,
  Alert,
  Popconfirm,
  Select,
  Dropdown,
} from "antd";
import useTransfer from "../hooks/useTransfer";
import useCompanies from "../hooks/useCompanies";
import useUsers from "../hooks/useUsers";
import dayjs from "dayjs";
import { ChevronLeft, Pencil, Settings, Trash2 } from "lucide-react";
import { useNavigate } from "react-router";
import useParseString from "../hooks/useParseString";
import PageDocumentation from "../components/PageDocumentation";

function TransferHistory() {
  const navigate = useNavigate();

  const { formatCurrency } = useParseString();
  const {
    transfers,
    getTransfers,
    loading: transferLoading,
    deleteTransfer,
  } = useTransfer();
  const { getCompanies, companies, loading: companiesLoading } = useCompanies();
  const { getUsers, users, loading: usersLoading } = useUsers();
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("day"),
    dayjs().endOf("day"),
  ]);

  const [totalAmount, setTotalAmount] = useState(0);
  const [params, setParams] = useState({
    page: 1,
    start_date: dayjs().format("YYYY-MM-DD"),
    end_date: dayjs().format("YYYY-MM-DD"),
  });

  // Fetch companies when component mounts
  useEffect(() => {
    getCompanies();
    getUsers();
  }, []);

  useEffect(() => {
    if (!transfers || !transfers.results) return;

    const totalAmount = transfers.results.reduce(
      (acc, transfer) => (acc += transfer.totalAmount),
      0
    );

    setTotalAmount(totalAmount);
  }, [transfers]);

  useEffect(() => {
    if (!dateRange) return;

    const formattedStartDate = dateRange[0].format("YYYY-MM-DD");
    const formattedEndDate = dateRange[1].format("YYYY-MM-DD");

    setParams({
      ...params,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    });
  }, [dateRange]);

  useEffect(() => {
    getTransfers(params);
  }, [params]);

  const handleTodayClick = () => {
    setDateRange([dayjs().startOf("day"), dayjs().endOf("day")]);
  };

  const isToday =
    dateRange[0].isSame(dayjs().startOf("day"), "day") &&
    dateRange[1].isSame(dayjs().endOf("day"), "day");

  const handleTableChange = (pagination) => {
    setParams({
      ...params,
      page: pagination,
    });
  };

  const handleCompanyFilter = (companyId) => {
    setParams({
      ...params,
      company_id: companyId || undefined, // remove param if empty
      page: 1, // reset to first page when filtering
    });
  };

  const resetCompanyFilter = () => {
    setParams({
      ...params,
      company_id: undefined,
      page: 1,
    });
  };

  const handleUserFilter = (userId) => {
    setParams({
      ...params,
      user_id: userId || undefined, // remove param if empty
      page: 1, // reset to first page when filtering
    });
  };

  const resetUserFilter = () => {
    setParams({
      ...params,
      user_id: undefined,
      page: 1,
    });
  };

  const columns = [
    {
      title: "Emisor",
      dataIndex: ["sender", "name"],
      key: "sender",
    },
    {
      title: "Receptor",
      dataIndex: ["receiver", "name"],
      key: "receiver",
      filterDropdown: () => (
        <div className="p-2">
          <div className="mb-2">
            <strong>Filtros</strong>
          </div>

          <div className="mb-3">
            <div className="font-medium mb-1">Empresa</div>
            <Select
              placeholder="Seleccionar empresa"
              style={{ width: 250 }}
              allowClear
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              onChange={handleCompanyFilter}
              value={params.company_id}
              options={companies.results?.map((company) => ({
                value: company.id,
                label: company.name,
              }))}
            />
            {params.company_id && (
              <div className="mt-2 text-left">
                <Button
                  type="primary"
                  onClick={resetCompanyFilter}
                  size="small"
                >
                  Limpiar filtro empresa
                </Button>
              </div>
            )}
          </div>

          <div>
            <div className="font-medium mb-1">Usuario</div>
            <Select
              placeholder="Seleccionar usuario"
              style={{ width: 250 }}
              allowClear
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              onChange={handleUserFilter}
              value={params.user_id}
              options={users?.map((user) => ({
                value: user.id,
                label: `${user.firstName} ${user.lastName} (${user.userType})`,
              }))}
            />
            {params.user_id && (
              <div className="mt-2 text-left">
                <Button type="primary" onClick={resetUserFilter} size="small">
                  Limpiar filtro usuario
                </Button>
              </div>
            )}
          </div>
        </div>
      ),
      filteredValue: params.company_id || params.user_id ? [1] : null,
    },
    {
      title: "Descripción",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Fecha",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => dayjs(text).format("ddd DD MMM"),
    },
    {
      title: "Comision",
      dataIndex: "commissionAmount",
      key: "commissionAmount",
      render: (text, record) => (
        <span className={text < 0 ? "text-red-500" : "text-green-500"}>
          {formatCurrency(text)}
        </span>
      ),
    },
    {
      title: "Ingresos",
      dataIndex: "incomeAmount",
      key: "incomeAmount",
      render: (text, record) => <span>{formatCurrency(text)}</span>,
    },
    {
      title: "Gastos",
      dataIndex: "expensesAmount",
      key: "expensesAmount",
      render: (text, record) => <span>{formatCurrency(text)}</span>,
    },
    {
      title: "Cantidad rendida",
      dataIndex: "totalAmount",
      key: "amount",
      render: (text, record) => (
        <span className={text < 0 ? "text-red-500" : "text-green-500"}>
          {formatCurrency(text)}
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      render: (text, record) => {
        const items = [
          {
            key: "edit",
            label: (
              <div
                className="flex items-center"
                onClick={() => {
                  navigate(`/transfers/${record.id}/detail`);
                }}
              >
                <Pencil size={16} className="me-2" />
                <span>Editar</span>
              </div>
            ),
          },
          {
            key: "delete",
            danger: true,
            label: (
              <Popconfirm
                title={`Borrar transferencia`}
                description="¿Estás seguro?"
                onConfirm={() => handleDeleteTransfer(record)}
                onCancel={() => {}}
                okText="Yes"
                cancelText="No"
              >
                <div className="flex items-center">
                  <Trash2 size={16} className="me-2" />
                  <span>Delete</span>
                </div>
              </Popconfirm>
            ),
          },
        ];

        return (
          <Dropdown menu={{ items }} trigger={["click"]}>
            <Button type="text" icon={<Settings size={20} />} />
          </Dropdown>
        );
      },
    },
  ];

  const handleDeleteTransfer = async (record) => {
    await deleteTransfer(record.id);
  };

  return (
    <>
      <div className="flex items-center">
        <Button
          onClick={() => {
            navigate("/transfers/debts");
          }}
          type="text"
          icon={<ChevronLeft size={16} />}
          className="me-2"
        >
          Volver
        </Button>

        <span className="font-semibold text-lg">
          Historial de transferencias
        </span>
        <PageDocumentation docKey="transfer-history" />
      </div>

      <div className="flex flex-col my-3">
        <span className="font-semibold text-md me-2">Filtros</span>

        <div className="">
          <DatePicker.RangePicker
            value={dateRange}
            onChange={setDateRange}
            allowClear={false}
          />
          {!isToday && (
            <Button type="link" onClick={handleTodayClick}>
              Ver transferencias de hoy
            </Button>
          )}
        </div>
      </div>
      {isToday && (
        <Alert
          message="Estas viendo las transferencias de Hoy"
          type="info"
          className="mb-3"
          showIcon
        />
      )}

      <Table
        dataSource={transfers?.results}
        columns={columns}
        size="small"
        rowKey="id"
        loading={transferLoading.get}
        pagination={{
          current: params.page,
          pageSize: transfers.results?.length,
          total: transfers?.count,
          onChange: handleTableChange,
        }}
        footer={() => {
          return (
            <div className="flex justify-between">
              <span className="font-semibold">Total rendido</span>
              <span
                className={
                  totalAmount < 0
                    ? "text-red-500 font-bold"
                    : "text-green-500 font-bold"
                }
              >
                {formatCurrency(totalAmount)}
              </span>
            </div>
          );
        }}
      />
    </>
  );
}

export default TransferHistory;
